import { ArrowRightCircleLine, Reload, Trash } from '@/icons';
import { BoxQuantity } from '@/atoms';

import { BundleError } from '@/components/providers';
import { useBundle, useBundleDispatch } from '@/lib/hooks';
import { gtmPush } from '@/lib/utils';
import { Button, CardProduct, SliderProduct } from '..';
import CardAddProduct from '../card-add-product';

import type { TypeClassName } from '@/components/types';
import type { PropsWithChildren } from '@/types';
import type { FunctionComponent } from 'react';
import type { TypeBundleOptionsProps } from './types';

const Heading = ({
  count,
  children,
  className,
}: PropsWithChildren<TypeClassName & { count: number }>) => (
  <h2 className={`u-subtitle u-subtitle--s flex flex-row gap-2 ${className}`}>
    <i className="u-label u-label--xs grid h-6 w-6 place-items-center rounded-full bg-primary-900 text-center font-bold not-italic text-white">
      {count}
    </i>
    <div className="flex flex-col">{children}</div>
  </h2>
);

/**
 * BundleOptions
 */
export const BundleOptions: FunctionComponent<TypeBundleOptionsProps> = ({
  onCardAddProduct,
}: TypeBundleOptionsProps) => {
  const dispatch = useBundleDispatch();
  const bundle = useBundle();

  if (!dispatch || !bundle)
    throw new BundleError('BundleCart must be inside a BundleProvider');

  const { numProducts, selectedProducts, selectedVariant, variants } = bundle;
  const { changeVariant, subtract } = dispatch;

  const onClick = () => {
    if (numProducts === 0) {
      gtmPush({
        event: 'custom_box',
        action: 'create_custom_box',
      });
    }

    onCardAddProduct();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Heading count={1} className="mb-3">
        Selecciona un tamaño
      </Heading>

      <div className="mb-12 ml-8 flex gap-2">
        {variants.map((item) => (
          <BoxQuantity
            variant={
              item.quantity === selectedVariant.quantity ? 'orange' : 'gray'
            }
            key={item.quantity}
            onClick={() => changeVariant(item)}
            role="button"
            width={60}
          >
            {item.quantity}
          </BoxQuantity>
        ))}
      </div>

      <Heading count={2} className="mb-4">
        Selecciona los productos
        {numProducts === 0 && (
          <span className="u-body u-body--xs normal-case text-typo-secondary">
            Pulsa sobre las botellas para comenzar
          </span>
        )}
      </Heading>

      <SliderProduct
        perPage={3}
        breakpoints={{
          1024: {
            perPage: 3,
          },
          900: {
            perPage: 4,
            padding: 40,
          },
          768: {
            padding: 40,
            perPage: 3,
          },
          390: {
            padding: 40,
            perPage: 2,
          },
          300: {
            padding: 40,
            perPage: 1,
          },
        }}
        className="-mx-4 mb-4 px-4 lg:mx-0 lg:px-0"
      >
        {[
          ...selectedProducts
            .map(
              ({
                id,
                image,
                name,
                uri,
                quantity,
                type,
                primaryCategory,
                price,
                regularPrice,
                stockStatus,
              }) =>
                Array.from(Array(quantity).keys()).map((_, index) => (
                  <CardProduct
                    productID={id}
                    href={uri}
                    type={type}
                    key={`${id}-${index}`}
                    stock={stockStatus}
                    editable={false}
                  >
                    <CardProduct.Image src={image.src} alt={image.alt} />
                    <CardProduct.Category href={primaryCategory.path}>
                      {primaryCategory.name}
                    </CardProduct.Category>

                    <CardProduct.Name>{name}</CardProduct.Name>

                    {price && (
                      <CardProduct.PriceDiscounted>
                        {regularPrice}
                      </CardProduct.PriceDiscounted>
                    )}

                    <button
                      className="absolute right-3 top-3 z-10 hover:text-primary-600"
                      onClick={() => subtract(id)}
                    >
                      <Trash width={18} />
                    </button>
                  </CardProduct>
                )),
            )
            .flat(),
          ...Array.from(
            Array(selectedVariant.quantity - numProducts).keys(),
          ).map((_, index) => (
            <CardAddProduct
              key={index}
              className="min-h-[312px] w-full"
              onClick={onClick}
            />
          )),
        ]}
      </SliderProduct>

      <Button
        variant={numProducts === 0 ? 'primary' : 'secondary'}
        icon={
          numProducts === 0 ? (
            <ArrowRightCircleLine className="transition-transform group-hover:translate-x-1" />
          ) : (
            <Reload
              width={16}
              height={16}
              className="transition-transform group-hover:rotate-90"
            />
          )
        }
        className="group mb-8 ml-auto !max-w-none sm:!max-w-max"
        onClick={onClick}
      >
        {numProducts === 0 ? 'comenzar' : 'Cambiar selección'}
      </Button>
    </>
  );
};

BundleOptions.displayName = 'BundleOptions';
